import Cart from 'cart-store';

const ELEMENT_NAME = 'product-editorial';
// Keep in sync with gallery_srcset_widths / gallery_sizes in sections/product-editorial.liquid.
const GALLERY_WIDTHS = [600, 750, 900, 1080, 1200, 1400];
const GALLERY_SIZES = '(max-width: 599px) 100vw, (max-width: 989px) 50vw, 32vw';
// Only slide 1 loads eagerly (see gallery_eager_slides in sections/product-editorial.liquid).
const GALLERY_EAGER_SLIDES = 1;
// Separators a locale may put inside the placeholder count of the stock messages ("987.654.321").
const COUNT_SEPARATOR_PATTERN = '[.,\\s\\u00a0\\u202f]?';

const GALLERY_DRAG_THRESHOLD_PX = 6;
const GALLERY_EDGE_TOLERANCE_PX = 2;
const GALLERY_FRAME_MS = 16;
const GALLERY_GLIDE_FRICTION = 0.92;
const GALLERY_MIN_GLIDE_PX = 0.6;
const GALLERY_SETTLE_MS = 450;
const THUMB_WIDTH = 200;
const THUMB_INDEX_TOKEN = '__INDEX__';
const ZOOM_WIDTH = 2400;
// Model badge sits on the first photo from the 2nd gallery slot on (0-based index 1).
const MODEL_BADGE_MIN_INDEX = 1;
const USP_DEFAULT_INTERVAL_MS = 4000;
const USP_MIN_INTERVAL_MS = 2000;
const USP_SWIPE_THRESHOLD_PX = 40;
const ZOOM_FALLBACK_RATIO = 4 / 3;
// A press that moves further than this is a scroll/drag, not a click to zoom.
const ZOOM_CLICK_TOLERANCE_PX = 8;

const pad = (value) => String(value).padStart(2, '0');

class ProductEditorial extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();

    try {
      this.variants = JSON.parse(this.querySelector('[data-pe-variants]')?.textContent || '[]');
    } catch (error) {
      console.error(`<${ELEMENT_NAME}>: invalid variant JSON`, error);
      this.variants = [];
    }
    try {
      const galleryJson = this.querySelector('[data-pe-gallery-json]')?.textContent;
      this.galleries = galleryJson ? JSON.parse(galleryJson) : null;
    } catch (error) {
      console.error(`<${ELEMENT_NAME}>: invalid gallery JSON`, error);
      this.galleries = null;
    }

    this.colorIndex = Number(this.dataset.colorIndex);
    this.form = this.querySelector('.pe__form');
    // The buy button and pickers are blocks that may sit outside the form; the button submits it via form="".
    this.submitButton = this.querySelector('[data-pe-submit]');
    this.variantInput = this.querySelector('[data-pe-variant-input]');
    this.submitLabel = this.querySelector('[data-pe-submit-label]');
    this.errorEl = this.querySelector('[data-pe-error]');
    this.priceEl = this.querySelector('[data-pe-price]');
    this.mainGroup = this.querySelector('[data-pe-group="main"]');
    this.secondGroup = this.querySelector('[data-pe-group="second"]');
    this.dealToggle = this.querySelector('[data-pe-deal-toggle]');
    this.dealPanel = this.querySelector('[data-pe-deal-panel]');
    this.sizeChart = this.querySelector('[data-pe-size-chart]');
    this.inventoryNotice = this.querySelector(`#inventory-notice-${this.dataset.sectionId}`);
    this.currentColor = this.colorIndex >= 0 ? this.selection(this.mainGroup)[this.colorIndex] : null;
    this.warmedColors = new Set([this.currentColor]);

    if (this.variantInput) this.variantInput.disabled = false;

    const options = { signal: this.controller.signal };
    this.addEventListener('change', (event) => this.onOptionChange(event), options);
    // Start loading a color's first photo on hover/touch, before the change event, so the swap shows sooner.
    this.mainGroup?.addEventListener('pointerover', (event) => this.onColorIntent(event), options);
    this.mainGroup?.addEventListener('pointerdown', (event) => this.onColorIntent(event), options);
    this.mainGroup?.addEventListener('focusin', (event) => this.onColorIntent(event), options);
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), options);
    this.dealToggle?.addEventListener('click', () => this.toggleDeal(), options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-pe-size-chart-open]')) this.sizeChart?.showModal();
      if (event.target.closest('[data-pe-size-chart-close]')) this.sizeChart?.close();
    }, options);
    this.sizeChart?.addEventListener('click', (event) => {
      if (event.target === this.sizeChart) this.sizeChart.close();
    }, options);

    [this.mainGroup, this.secondGroup].forEach((group) => this.refreshGroup(group));
    this.setupStickyBar();
    this.setupUspSliders();
    this.refreshMain({ updateUrl: false });
    this.observeVideos();
    this.setupGallerySlider();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.videoObserver?.disconnect();
    this.videoObserver = null;
    this.galleryResizeObserver?.disconnect();
    this.galleryResizeObserver = null;
    this.lightbox?.destroy();
    this.lightbox = null;
    this.barObserver?.disconnect();
    this.barObserver = null;
    this.uspCleanups?.forEach((cleanup) => cleanup());
    this.uspCleanups = [];
  }

  /* USP slider under the buy button: auto-advances while on screen, arrows and swipe, pauses on hover/focus. */
  setupUspSliders() {
    this.uspCleanups = [];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('[data-pe-usp]').forEach((slider) => {
      const slides = [...slider.querySelectorAll('[data-pe-usp-slide]')];
      const track = slider.querySelector('[data-pe-usp-track]');
      if (slides.length < 2) return;

      const options = { signal: this.controller.signal };
      const interval = Math.max(Number(slider.dataset.interval) || USP_DEFAULT_INTERVAL_MS, USP_MIN_INTERVAL_MS);
      const autoplay = slider.dataset.autoplay === 'true' && !reduceMotion;
      let current = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      let timer = 0;
      let paused = false;
      let visible = false;
      let touchStartX = null;

      const goTo = (index) => {
        slides[current].classList.remove('is-active');
        slides[current].setAttribute('aria-hidden', 'true');
        current = (index + slides.length) % slides.length;
        slides[current].classList.add('is-active');
        slides[current].removeAttribute('aria-hidden');
      };
      const stop = () => {
        window.clearInterval(timer);
        timer = 0;
      };
      // Hidden (display: none on desktop) or off-screen sliders never intersect, so they do not tick.
      const start = () => {
        stop();
        if (autoplay && visible && !paused) timer = window.setInterval(() => goTo(current + 1), interval);
      };
      const step = (direction) => {
        // Announce manual changes; automatic ones stay silent.
        track?.setAttribute('aria-live', 'polite');
        goTo(current + direction);
        start();
      };
      const setPaused = (value) => {
        paused = value;
        start();
      };

      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        start();
      });
      observer.observe(slider);

      slider.querySelectorAll('[data-pe-usp-dir]').forEach((button) => {
        button.addEventListener('click', () => step(Number(button.dataset.peUspDir) || 1), options);
      });
      slider.addEventListener('mouseenter', () => setPaused(true), options);
      slider.addEventListener('mouseleave', () => setPaused(false), options);
      slider.addEventListener('focusin', () => setPaused(true), options);
      slider.addEventListener('focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) setPaused(false);
      }, options);
      slider.addEventListener('touchstart', (event) => {
        touchStartX = event.touches[0]?.clientX ?? null;
      }, { ...options, passive: true });
      slider.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const distance = touchStartX - (event.changedTouches[0]?.clientX ?? touchStartX);
        touchStartX = null;
        if (Math.abs(distance) > USP_SWIPE_THRESHOLD_PX) step(distance > 0 ? 1 : -1);
      }, { ...options, passive: true });

      this.uspCleanups.push(() => {
        stop();
        observer.disconnect();
      });
    });
  }

  /* Small screens: a bottom bar mirrors the main button while that button is out of view (above or below). */
  setupStickyBar() {
    this.bar = this.querySelector('[data-pe-bar]');
    this.barButton = this.querySelector('[data-pe-bar-button]');
    this.barLabel = this.querySelector('[data-pe-bar-label]');
    this.barPrice = this.querySelector('[data-pe-bar-price]');
    const query = this.dataset.stickyBarQuery;
    if (!this.bar || !this.barButton || !query) return;
    if (!this.submitButton) {
      console.warn(`<${ELEMENT_NAME}>: sticky bar needs the buy button block`);
      return;
    }

    const options = { signal: this.controller.signal };
    const media = window.matchMedia(query);
    let mainButtonVisible = true;
    const update = () => this.toggleStickyBar(media.matches && !mainButtonVisible);

    this.barObserver = new IntersectionObserver(([entry]) => {
      mainButtonVisible = entry.isIntersecting;
      update();
    });
    this.barObserver.observe(this.submitButton);
    media.addEventListener('change', update, options);
    this.barButton.addEventListener('click', () => this.onStickyBarClick(), options);
  }

  toggleStickyBar(show) {
    if (!this.bar) return;
    this.bar.classList.toggle('is-visible', show);
    this.bar.toggleAttribute('inert', !show);
    this.bar.setAttribute('aria-hidden', String(!show));
  }

  onStickyBarClick() {
    const state = this.submitState();
    if (state.ready) {
      // A real click (not requestSubmit) so bundle apps listening on the buy button still intercept it.
      this.submitButton.click();
      return;
    }
    // Options still missing: bring the pickers (and the main button with its hint) into view.
    const target = this.mainGroup || this.submitButton;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  syncStickyBar(state) {
    if (!this.bar) return;
    if (this.barLabel && state.label) this.barLabel.textContent = state.label;
    const busy = this.submitButton?.getAttribute('aria-busy') === 'true';
    this.barButton.disabled = state.soldOut || busy;
    this.barButton.toggleAttribute('aria-busy', busy);
  }

  /* Below 990px the gallery is a horizontal slider: arrows, mouse drag and touch swipe. On desktop it is a grid
     without overflow, so the arrows stay hidden automatically. */
  setupGallerySlider() {
    this.galleryEl = this.querySelector('[data-pe-gallery]');
    this.galleryGrid = this.querySelector('[data-pe-gallery-grid]');
    this.galleryPrev = this.querySelector('[data-pe-gallery-prev]');
    this.galleryNext = this.querySelector('[data-pe-gallery-next]');
    if (!this.galleryGrid) return;

    const options = { signal: this.controller.signal };
    const grid = this.galleryGrid;
    this.galleryPrev?.addEventListener('click', () => this.scrollGallery(-1), options);
    this.galleryNext?.addEventListener('click', () => this.scrollGallery(1), options);
    grid.addEventListener('scroll', () => this.updateGalleryNav(), { ...options, passive: true });
    grid.addEventListener('dragstart', (event) => event.preventDefault(), options);

    // Mouse drag with momentum: glide after release, then ease onto the nearest slide (touch uses native scrolling).
    let drag = null;
    let glideFrame = 0;
    const stopGlide = () => {
      window.cancelAnimationFrame(glideFrame);
      glideFrame = 0;
    };
    grid.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || grid.scrollWidth <= grid.clientWidth) return;
      stopGlide();
      drag = { x: event.clientX, left: grid.scrollLeft, id: event.pointerId, moved: false, lastX: event.clientX, lastT: event.timeStamp, velocity: 0 };
    }, options);
    grid.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && Math.abs(delta) < GALLERY_DRAG_THRESHOLD_PX) return;
      if (!drag.moved) {
        drag.moved = true;
        grid.setPointerCapture(event.pointerId);
        this.galleryEl?.setAttribute('data-dragging', '');
      }
      const elapsed = Math.max(1, event.timeStamp - drag.lastT);
      drag.velocity = (event.clientX - drag.lastX) / elapsed;
      drag.lastX = event.clientX;
      drag.lastT = event.timeStamp;
      grid.scrollLeft = drag.left - delta;
    }, options);
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { moved, velocity } = drag;
      drag = null;
      if (!moved) return;
      if (grid.hasPointerCapture(event.pointerId)) grid.releasePointerCapture(event.pointerId);
      this.glideGallery(velocity * GALLERY_FRAME_MS, (frame) => {
        glideFrame = frame;
      });
    };
    grid.addEventListener('pointerup', endDrag, options);
    grid.addEventListener('pointercancel', endDrag, options);

    this.setupZoom(options);
    this.setupGalleryThumbs(options);

    this.galleryResizeObserver = new ResizeObserver(() => this.updateGalleryNav());
    this.galleryResizeObserver.observe(grid);
    this.updateGalleryNav();
  }

  setupZoom(options) {
    const grid = this.galleryGrid;
    let press = null;
    grid.addEventListener('pointerdown', (event) => {
      press = { x: event.clientX, y: event.clientY };
    }, options);
    grid.addEventListener('click', (event) => {
      const slide = event.target.closest('[data-pe-zoom]');
      if (!slide || !press) return;
      const moved = Math.hypot(event.clientX - press.x, event.clientY - press.y);
      press = null;
      if (moved > ZOOM_CLICK_TOLERANCE_PX || this.galleryEl?.hasAttribute('data-dragging')) return;
      this.openZoom(slide);
    }, options);
    grid.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const slide = event.target.closest('[data-pe-zoom]');
      if (!slide) return;
      event.preventDefault();
      this.openZoom(slide);
    }, options);
  }

  /* "Main photo with thumbnails" gallery style: the slider shows one photo at a time and the row below jumps to one.
     The row is rebuilt from the slides, so it follows colour changes (renderGallery) without its own data. */
  setupGalleryThumbs(options) {
    this.thumbsEl = this.querySelector('[data-pe-thumbs]');
    if (!this.thumbsEl) return;
    this.thumbsEl.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-pe-thumb]');
      if (thumb) this.showSlide(Number(thumb.dataset.peThumb));
    }, options);
    let frame = 0;
    this.galleryGrid.addEventListener('scroll', () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        this.syncThumbs();
      });
    }, { ...options, passive: true });
    this.renderThumbs();
  }

  renderThumbs() {
    if (!this.thumbsEl || !this.galleryGrid) return;
    const label = this.dataset.labelShowImage || '';
    const thumbs = [...this.galleryGrid.children].map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pe__thumb';
      button.dataset.peThumb = String(index);
      button.setAttribute('aria-label', label.replace(THUMB_INDEX_TOKEN, String(index + 1)));
      const src = this.thumbSource(slide);
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.width = THUMB_WIDTH;
        img.height = THUMB_WIDTH;
        img.draggable = false;
        button.append(img);
      }
      return button;
    });
    this.thumbsEl.replaceChildren(...thumbs);
    this.thumbsEl.hidden = thumbs.length < 2;
    this.activeThumb = -1;
    this.syncThumbs();
  }

  thumbSource(slide) {
    const img = slide.querySelector('img');
    if (!img) return slide.querySelector('video')?.poster || '';
    try {
      const url = new URL(img.getAttribute('src'), window.location.origin);
      url.searchParams.set('width', THUMB_WIDTH);
      return url.toString();
    } catch {
      return img.src;
    }
  }

  showSlide(index) {
    const grid = this.galleryGrid;
    const slide = grid?.children[index];
    if (!slide) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    grid.scrollTo({ left: slide.offsetLeft - grid.offsetLeft, behavior });
  }

  syncThumbs() {
    const strip = this.thumbsEl;
    const grid = this.galleryGrid;
    const slide = grid?.firstElementChild;
    if (!strip || strip.hidden || !slide) return;
    const gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const index = step > 0 ? Math.round(grid.scrollLeft / step) : 0;
    if (index === this.activeThumb) return;
    this.activeThumb = index;
    [...strip.children].forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    // More thumbnails than fit: scroll the row (not the page) so the active one stays in view.
    const active = strip.children[index];
    if (!active || strip.scrollWidth <= strip.clientWidth) return;
    const left = active.offsetLeft - strip.offsetLeft;
    const right = left + active.offsetWidth;
    if (left < strip.scrollLeft) strip.scrollTo({ left });
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollTo({ left: right - strip.clientWidth });
  }

  zoomSource(slide, img) {
    if (slide.dataset.zoomSrc) return slide.dataset.zoomSrc;
    try {
      const url = new URL(img.currentSrc || img.src, window.location.origin);
      url.searchParams.set('width', ZOOM_WIDTH);
      return url.toString();
    } catch {
      return img.currentSrc || img.src;
    }
  }

  // Lightbox buttons use the same thin stroke icons as the gallery slider arrows.
  static zoomIcon(path) {
    return `<svg class="pswp__icn" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${path}</svg>`;
  }

  zoomItem(slide) {
    const video = slide.querySelector('video');
    if (video) {
      // Videos open as a playing, looping, silent clip sized to fit the screen.
      const wrapper = document.createElement('div');
      wrapper.className = 'pe-zoom-video';
      const clip = document.createElement('video');
      clip.src = video.currentSrc || video.src || video.querySelector('source')?.src || '';
      if (video.poster) clip.poster = video.poster;
      ['autoplay', 'muted', 'loop', 'playsinline'].forEach((attr) => clip.setAttribute(attr, ''));
      clip.setAttribute('preload', 'auto');
      if (!clip.getAttribute('src')) console.warn(`<${ELEMENT_NAME}>: gallery video has no source to show in the lightbox`);
      wrapper.append(clip);
      return { html: wrapper.outerHTML, slide };
    }
    const img = slide.querySelector('img');
    if (!img) return null;
    const ratio = img.naturalWidth ? img.naturalHeight / img.naturalWidth : ZOOM_FALLBACK_RATIO;
    return { src: this.zoomSource(slide, img), width: ZOOM_WIDTH, height: Math.round(ZOOM_WIDTH * ratio), alt: img.alt, element: img, slide };
  }

  async openZoom(activeSlide) {
    const slides = [...this.galleryGrid.querySelectorAll('[data-pe-zoom]')];
    const items = slides.map((slide) => this.zoomItem(slide)).filter(Boolean);
    const index = items.findIndex((item) => item.slide === activeSlide);
    if (index < 0) return;

    try {
      const { default: PhotoSwipeLightbox } = await import(this.dataset.pswpLightbox);
      const icon = ProductEditorial.zoomIcon;
      this.lightbox?.destroy();
      this.lightbox = new PhotoSwipeLightbox({
        dataSource: items,
        bgOpacity: 1,
        counter: true,
        mainClass: 'pe-zoom',
        // A plain fade: the zoom-back animation cannot match cropped (object-fit: cover) gallery tiles.
        showHideAnimationType: 'fade',
        closeSVG: icon('<path d="M6 6l12 12M18 6 6 18"/>'),
        arrowPrevSVG: icon('<path d="m15 5-7 7 7 7"/>'),
        arrowNextSVG: icon('<path d="m9 5 7 7-7 7"/>'),
        zoomSVG: icon('<circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2M8.5 11h5M11 8.5v5"/>'),
        pswpModule: () => import(this.dataset.pswpCore),
      });
      this.lightbox.addFilter('placeholderSrc', (placeholder, slide) => slide.data.element?.currentSrc || placeholder);
      // Clips injected as HTML lose their "muted" state, so browsers block autoplay; mute and start them in code.
      // Only the visible clip plays.
      const syncVideos = () => {
        const pswp = this.lightbox.pswp;
        pswp?.element?.querySelectorAll('.pe-zoom-video video').forEach((clip) => {
          const active = pswp.currSlide?.container?.contains(clip);
          if (active) {
            clip.muted = true;
            clip.playsInline = true;
            clip.play().catch((error) => {
              // Autoplay blocked (e.g. battery saver): show controls so the clip can still be started by hand.
              clip.controls = true;
              console.warn(`<${ELEMENT_NAME}>: lightbox video could not autoplay`, error);
            });
          } else {
            clip.pause();
          }
        });
      };
      this.lightbox.on('change', syncVideos);
      this.lightbox.on('contentAppend', () => window.requestAnimationFrame(syncVideos));
      this.lightbox.on('openingAnimationEnd', syncVideos);
      // Keep the slider in step with the item viewed in the lightbox.
      this.lightbox.on('close', () => {
        const current = items[this.lightbox.pswp?.currIndex ?? index];
        if (current && this.galleryGrid.scrollWidth > this.galleryGrid.clientWidth) {
          this.galleryGrid.scrollTo({ left: current.slide.offsetLeft - this.galleryGrid.offsetLeft, behavior: 'auto' });
        }
      });
      this.lightbox.init();
      this.lightbox.loadAndOpen(index);
    } catch (error) {
      console.error(`<${ELEMENT_NAME}>: could not open image zoom`, error);
    }
  }

  glideGallery(initialSpeed, setFrame) {
    const grid = this.galleryGrid;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let speed = reduced ? 0 : initialSpeed;
    const step = () => {
      if (Math.abs(speed) > GALLERY_MIN_GLIDE_PX) {
        grid.scrollLeft -= speed;
        speed *= GALLERY_GLIDE_FRICTION;
        setFrame(window.requestAnimationFrame(step));
        return;
      }
      this.settleGallery();
    };
    step();
  }

  // Ease onto the closest slide, then hand scrolling back to CSS snapping without a visible jump.
  settleGallery() {
    const grid = this.galleryGrid;
    const slide = grid?.firstElementChild;
    if (!grid || !slide) return;
    const gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;
    const stepWidth = slide.getBoundingClientRect().width + gap;
    const maxScroll = grid.scrollWidth - grid.clientWidth;
    const target = Math.min(maxScroll, Math.round(grid.scrollLeft / stepWidth) * stepWidth);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    grid.scrollTo({ left: target, behavior });
    window.setTimeout(() => this.galleryEl?.removeAttribute('data-dragging'), GALLERY_SETTLE_MS);
  }

  scrollGallery(direction) {
    const grid = this.galleryGrid;
    const slide = grid?.firstElementChild;
    if (!grid || !slide) return;
    const gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    grid.scrollBy({ left: direction * (slide.getBoundingClientRect().width + gap), behavior });
  }

  updateGalleryNav() {
    const grid = this.galleryGrid;
    if (!grid) return;
    const maxScroll = grid.scrollWidth - grid.clientWidth;
    const scrollable = maxScroll > GALLERY_EDGE_TOLERANCE_PX;
    [this.galleryPrev, this.galleryNext].forEach((button) => {
      if (button) button.hidden = !scrollable;
    });
    this.galleryEl?.toggleAttribute('data-scrollable', scrollable);
    if (!scrollable) return;
    if (this.galleryPrev) this.galleryPrev.disabled = grid.scrollLeft <= GALLERY_EDGE_TOLERANCE_PX;
    if (this.galleryNext) this.galleryNext.disabled = grid.scrollLeft >= maxScroll - GALLERY_EDGE_TOLERANCE_PX;
  }

  /* Gallery videos loop silently without controls, and only play while on screen. */
  observeVideos() {
    this.videoObserver?.disconnect();
    const videos = [...this.querySelectorAll('.pe__media video')];
    if (!videos.length) return;
    videos.forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.removeAttribute('controls');
    });
    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => video.play().catch(() => {}));
      return;
    }
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) target.play().catch((error) => console.warn(`<${ELEMENT_NAME}>: video autoplay blocked`, error));
        else target.pause();
      });
    }, { threshold: 0.25 });
    videos.forEach((video) => this.videoObserver.observe(video));
  }

  get dealOpen() {
    return Boolean(this.dealPanel && !this.dealPanel.hidden);
  }

  selection(group) {
    if (!group) return [];
    return [...group.querySelectorAll('[data-pe-option]')].map((fieldset) => {
      const checked = fieldset.querySelector('[data-pe-value]:checked');
      return checked ? checked.value : null;
    });
  }

  findVariant(selection) {
    if (!selection.length || selection.some((value) => value === null)) return null;
    return this.variants.find((variant) => variant.options.every((value, i) => value === selection[i])) || null;
  }

  /* Sizes (and other non-color options) are judged against the chosen color; colors against any size. */
  isValueAvailable(selection, optionIndex, value) {
    return this.variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== value) return false;
      if (optionIndex !== this.colorIndex && this.colorIndex >= 0 && selection[this.colorIndex]) {
        return variant.options[this.colorIndex] === selection[this.colorIndex];
      }
      return true;
    });
  }

  refreshGroup(group) {
    if (!group) return;
    const selection = this.selection(group);
    group.querySelectorAll('[data-pe-option]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.peOption);
      fieldset.querySelectorAll('[data-pe-value]').forEach((input) => {
        const available = this.isValueAvailable(selection, optionIndex, input.value);
        const label = input.nextElementSibling;
        label?.classList.toggle('is-unavailable', !available);
        label?.querySelector('[data-pe-sold-out-text]')?.toggleAttribute('hidden', available);
        // Sold-out sizes cannot be chosen; sold-out colors stay selectable so their photos can be viewed.
        if (optionIndex !== this.colorIndex) {
          input.disabled = !available;
          if (!available && input.checked) input.checked = false;
        }
      });
      const valueLabel = fieldset.querySelector('[data-pe-option-value]');
      if (valueLabel) valueLabel.textContent = selection[optionIndex] || '';
    });
  }

  onOptionChange(event) {
    const group = event.target.closest('[data-pe-group]');
    if (!group || !event.target.matches('[data-pe-value]')) return;
    this.clearError();
    this.refreshGroup(group);
    this.selectFirstAvailable(group);
    if (group === this.mainGroup) this.refreshMain({ updateUrl: true });
    else this.updateSubmit();
  }

  /* After a color change, keep the chosen size when it is in stock; otherwise pick the first available one. */
  selectFirstAvailable(group) {
    let changed = false;
    group.querySelectorAll('[data-pe-option]').forEach((fieldset) => {
      if (Number(fieldset.dataset.peOption) === this.colorIndex) return;
      if (fieldset.querySelector('[data-pe-value]:checked')) return;
      const first = fieldset.querySelector('[data-pe-value]:not(:disabled)');
      if (first) {
        first.checked = true;
        changed = true;
      }
    });
    if (changed) this.refreshGroup(group);
  }

  refreshMain({ updateUrl }) {
    const selection = this.selection(this.mainGroup);
    const variant = this.findVariant(selection);
    const color = this.colorIndex >= 0 ? selection[this.colorIndex] : null;

    if (variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      this.renderInventoryNotice(variant);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
    }

    const priceVariant = variant || this.variants.find((v) => color === null || v.options[this.colorIndex] === color) || this.variants[0];
    this.renderPrice(priceVariant);

    if (color !== this.currentColor) {
      this.currentColor = color;
      this.renderGallery(color);
    }
    this.updateSubmit();
  }

  /* Mirrors the 'inventory_notice' branch of snippets/pdp-blocks.liquid, using the stock fields of the variant JSON. */
  static inventoryState(variant, { limit, showQty }) {
    if (!variant.available) return { soldOut: true, low: false, showNumber: false };
    const qty = Number(variant.qty) || 0;
    const countable = variant.tracked && (!variant.oversell || qty > 0);
    return {
      soldOut: false,
      low: countable && qty <= limit,
      showNumber: countable && showQty,
      continueSelling: variant.oversell && qty < 1,
      qty,
    };
  }

  renderInventoryNotice(variant) {
    const notice = this.inventoryNotice;
    if (!notice || !('qty' in variant)) return;
    const note = notice.querySelector('.stock-note');
    const text = notice.querySelector('.stock-note__text');
    if (!note || !text) {
      console.warn(`<${ELEMENT_NAME}>: inventory notice markup changed, cannot update it`);
      return;
    }

    const { dataset } = notice;
    const state = ProductEditorial.inventoryState(variant, {
      limit: Number(dataset.inventoryLimit) || 0,
      showQty: dataset.showQty === 'true',
    });
    note.classList.toggle('stock-note--sold', state.soldOut);
    note.classList.toggle('stock-note--in-stock', !state.soldOut);
    note.classList.toggle('stock-note--low', state.low);

    if (state.showNumber && dataset.countToken) {
      const template = state.low ? dataset.textNumberLowStock : dataset.textNumberInStock;
      const token = new RegExp(dataset.countToken.split('').join(COUNT_SEPARATOR_PATTERN));
      // Locale strings ending in _html may contain markup; the only dynamic part is the numeric count.
      text.innerHTML = (template || '').replace(token, String(state.qty));
      return;
    }
    let message = dataset.textInStock;
    if (state.soldOut) message = dataset.textSoldOut;
    else if (state.low) message = dataset.textLowStock;
    else if (state.continueSelling) message = dataset.textContinueSelling;
    text.textContent = message || '';
  }

  renderPrice(variant) {
    if (!variant || (!this.priceEl && !this.barPrice)) return;
    const nodes = [];
    if (variant.compare) {
      const compare = document.createElement('s');
      compare.textContent = variant.compare;
      nodes.push(compare);
    }
    const price = document.createElement('span');
    price.textContent = variant.price;
    nodes.push(price);
    this.priceEl?.replaceChildren(...nodes);
    this.barPrice?.replaceChildren(...nodes.map((node) => node.cloneNode(true)));
  }

  /* Gallery JSON (snippets/gallery-media-groups.liquid, format 'editorial'): every media item once, and per
     color the indexes of its items in display order. */
  galleryMedia(color) {
    const media = this.galleries?.media;
    const indexes = color ? this.galleries?.groups?.[color] : null;
    if (!Array.isArray(media) || !Array.isArray(indexes)) return [];
    return indexes.map((index) => media[index]).filter(Boolean);
  }

  onColorIntent(event) {
    if (this.colorIndex < 0) return;
    const label = event.target.closest?.('label');
    const input = label?.control || (event.target.matches?.('[data-pe-value]') ? event.target : null);
    if (!input?.matches('[data-pe-value]')) return;
    const fieldset = input.closest('[data-pe-option]');
    if (Number(fieldset?.dataset.peOption) !== this.colorIndex) return;
    this.warmGallery(input.value);
  }

  // Fetches a color's first photo with the same srcset/sizes as the slide, so the browser picks the same file.
  warmGallery(color) {
    if (!color || this.warmedColors.has(color)) return;
    this.warmedColors.add(color);
    const first = this.galleryMedia(color).find((item) => item.type !== 'video');
    if (!first) return;
    const img = new Image();
    img.sizes = this.dataset.gallerySizes || GALLERY_SIZES;
    img.srcset = this.srcset(first.url);
    img.src = first.url;
  }

  renderGallery(color) {
    const grid = this.querySelector('[data-pe-gallery-grid]');
    const media = this.galleryMedia(color);
    if (!grid || !media.length) return;
    this.warmedColors.add(color);

    const total = pad(media.length);
    const badgeText = this.dataset.modelBadge?.trim();
    const badgeIndex = media.findIndex((item, index) => index >= MODEL_BADGE_MIN_INDEX && item.type !== 'video');
    const zoomHint = this.querySelector('template[data-pe-zoom-hint]')?.content;
    const items = media.map((item, index) => {
      const li = document.createElement('li');
      li.className = 'pe__media';
      if (item.type === 'video') {
        // Played (and so downloaded) by observeVideos() only once it scrolls into view.
        const video = document.createElement('video');
        Object.assign(video, { src: item.url, poster: item.poster || '', muted: true, loop: true, playsInline: true, preload: 'none' });
        li.append(video);
      } else {
        const img = document.createElement('img');
        img.alt = item.alt || '';
        img.width = 1400;
        img.height = 1867;
        if (index === 0) img.fetchPriority = 'high';
        else if (index >= GALLERY_EAGER_SLIDES) img.loading = 'lazy';
        // sizes and srcset before src, so the browser does not start a request for the fallback src first.
        img.sizes = this.dataset.gallerySizes || GALLERY_SIZES;
        img.srcset = this.srcset(item.url);
        img.src = item.url;
        li.append(img);
        if (badgeText && index === badgeIndex) {
          const badge = document.createElement('span');
          badge.className = 'pe__media-badge';
          badge.textContent = badgeText;
          li.append(badge);
        }
        if (zoomHint) li.append(zoomHint.cloneNode(true));
      }
      {
        li.dataset.peZoom = '';
        li.tabIndex = 0;
        li.setAttribute('role', 'button');
        li.setAttribute('aria-label', this.dataset.labelZoom || '');
      }
      const count = document.createElement('span');
      count.className = 'pe__media-count';
      count.setAttribute('aria-hidden', 'true');
      count.textContent = `${pad(index + 1)} / ${total}`;
      li.append(count);
      return li;
    });
    grid.replaceChildren(...items);
    grid.scrollTo?.({ left: 0 });
    this.renderThumbs();
    this.observeVideos();
    this.updateGalleryNav();
    this.scrollToGalleryStart();
  }

  // After a color change the shopper should see that color's first image, not the middle of the old set.
  scrollToGalleryStart() {
    const gallery = this.querySelector('[data-pe-gallery]');
    if (!gallery) return;
    const { top } = gallery.getBoundingClientRect();
    if (top >= 0) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    window.scrollBy({ top, behavior });
  }

  // Shopify CDN URLs accept a width parameter, so one URL yields the whole srcset.
  srcset(url) {
    try {
      return GALLERY_WIDTHS.map((width) => {
        const next = new URL(url, window.location.origin);
        next.searchParams.set('width', width);
        return `${next.toString()} ${width}w`;
      }).join(', ');
    } catch {
      return '';
    }
  }

  toggleDeal() {
    if (!this.dealPanel || !this.dealToggle) return;
    const open = this.dealPanel.hidden;
    this.dealPanel.hidden = !open;
    this.dealToggle.setAttribute('aria-expanded', String(open));
    this.clearError();
    this.updateSubmit();
  }

  submitState() {
    const mainSelection = this.selection(this.mainGroup);
    const mainVariant = this.findVariant(mainSelection);
    if (!mainVariant) {
      const onlySizeMissing = mainSelection.every((value, i) => value !== null || i !== this.colorIndex);
      return { ready: false, label: onlySizeMissing ? this.dataset.labelChooseSize : this.dataset.labelChooseOptions };
    }
    if (!mainVariant.available) return { ready: false, soldOut: true, label: this.dataset.labelSoldOut };
    const addLabel = this.submitButton?.dataset.labelAdd || this.dataset.labelAdd;
    if (!this.dealOpen) return { ready: true, label: addLabel, items: [mainVariant] };

    const secondVariant = this.findVariant(this.selection(this.secondGroup));
    if (!secondVariant) return { ready: false, label: this.dataset.labelChooseSecond };
    if (!secondVariant.available) return { ready: false, label: this.dataset.labelSoldOut };
    return { ready: true, label: this.dataset.labelAddPair, items: [mainVariant, secondVariant] };
  }

  updateSubmit() {
    if (!this.submitButton) return;
    const state = this.submitState();
    this.submitButton.disabled = !state.ready;
    if (this.submitLabel && state.label) this.submitLabel.textContent = state.label;
    this.syncStickyBar(state);
  }

  async onSubmit(event) {
    event.preventDefault();
    const state = this.submitState();
    if (!state.ready || this.submitButton?.getAttribute('aria-busy') === 'true') return;

    const items = this.dealOpen ? this.dealItems(state.items) : this.formItem();
    if (!items) {
      this.showError(this.dataset.labelError);
      return;
    }

    this.clearError();
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.disabled = true;
    this.syncStickyBar(state);
    try {
      await Cart.add(items);
      await Cart.resetVariantsBeingAdded?.();
      if (this.dataset.cartType === 'page') {
        window.location.href = this.dataset.cartUrl;
        return;
      }
      const cart = document.querySelector('.modal--cart, .popup--cart-notification');
      if (cart && !cart.shown) cart.open?.();
      else if (cart?.interval) {
        cart.interval.reset();
        cart.interval.start();
      }
    } catch (error) {
      console.error(`<${ELEMENT_NAME}>: add to cart failed`, error);
      this.showError(error?.message || this.dataset.labelError);
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.updateSubmit();
    }
  }

  // Two picks of the same variant become one line with quantity 2.
  dealItems(variants) {
    return variants.reduce((list, variant) => {
      const existing = list.find((item) => item.id === variant.id);
      if (existing) existing.quantity += 1;
      else list.push({ id: variant.id, quantity: 1 });
      return list;
    }, []);
  }

  /* The form is the source of truth outside the theme's own deal: bundle apps (Kaching Bundles) rewrite its
     id, quantity and properties[...] before the submit reaches us. Flat "properties[x]" keys are what
     cart-store expects, same as product-form.js. */
  formItem() {
    if (!this.form) return null;
    const item = Object.fromEntries(new FormData(this.form).entries());
    const id = Number(item.id);
    const quantity = Number(item.quantity || 1);
    if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity <= 0) {
      console.error(`<${ELEMENT_NAME}>: invalid form item`, item);
      return null;
    }
    return { ...item, id, quantity };
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, ProductEditorial);
